import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, Inbox, MessageSquare, RefreshCw, Search } from 'lucide-react';
import PanelSheet from '../../components/PanelSheet';
import SupportConversation from '../../components/support/SupportConversation';
import { API, SupportThread } from '../../components/support/supportTypes';
import SupportThreadList, { threadName } from './SupportThreadList';
import useWide from './useWide';

type Filter = 'open' | 'resolved' | 'all';
const FILTERS: { key: Filter; label: string }[] = [{ key: 'open', label: 'Açık' }, { key: 'resolved', label: 'Çözüldü' }, { key: 'all', label: 'Tümü' }];

export default function SupportPage() {
  const [threads, setThreads] = useState<SupportThread[]>([]);
  const [counts, setCounts] = useState({ open: 0, resolved: 0 });
  const [filter, setFilter] = useState<Filter>('open');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<number | null>(null);
  const [selectedName, setSelectedName] = useState('');
  const [waiting, setWaiting] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const wide = useWide();

  // Silent for the 30 s refresh and the conversation's own reloads: the
  // refresh button only spins when someone asked for it.
  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/threads`, { params: { status: filter, q: search || undefined } });
      setThreads(r.data.threads); setCounts(r.data.counts); setError('');
      // "Waiting" counts open threads with unread messages, so it is only
      // taken from the unfiltered open list (search would change it).
      if (filter === 'open' && !search) setWaiting(r.data.threads.filter((t: SupportThread) => t.unread > 0).length);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Konuşmalar alınamadı.');
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const id = setInterval(() => load(true), 30000); return () => clearInterval(id); }, [filter, search]);

  const open = (id: number) => {
    const thread = threads.find(item => item.id === id);
    setSelectedName(thread ? threadName(thread) : 'Konuşma'); setSelected(id);
  };
  const reloadQuietly = () => load(true);
  const stats = [
    ['Açık konuşma', counts.open, 'brand', Inbox], ['Cevap bekleyen', waiting, 'gold', MessageSquare], ['Çözüldü', counts.resolved, 'positive', CheckCircle],
  ] as const;

  return <div className="users-redesign support-page space-y-5">
    <header className="flex items-start justify-between gap-3"><div><h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Destek</h1><p className="hidden sm:block text-sm text-gray-500 mt-2">Kullanıcı geri bildirimlerini okuyun ve cevaplayın.</p></div>
      <button className="user-btn shrink-0" onClick={() => load()} disabled={loading} aria-label="Konuşmaları yenile"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /><span className="hidden sm:inline">Yenile</span></button></header>
    <div className="management-stats grid grid-cols-3 gap-2 sm:gap-3">{stats.map(([label, value, tone, Icon]) => <div key={label} className="management-stat" data-tone={tone}>
      <div className="text-gray-500 flex items-center gap-2 text-xs"><Icon size={15} className="hidden sm:block" />{label}</div>
      <div className="text-xl sm:text-2xl font-semibold mt-2 tracking-tight">{value.toLocaleString('tr-TR')}</div>
    </div>)}</div>
    {error && <div role="alert" className="bg-red-50 text-red-700 p-4 text-sm">{error}<button className="underline ml-2" onClick={() => load()}>Yeniden dene</button></div>}
    <section className="management-toolbar support-toolbar bg-white border border-gray-100" aria-label="Destek arama ve filtre">
      <div className="relative flex-1 min-w-0"><Search size={17} className="absolute left-3 top-3.5 text-gray-400" /><input className="management-search user-input pl-10" aria-label="Konuşmalarda ara" placeholder="Kullanıcı veya mesaj ara…" value={search} onChange={event => setSearch(event.target.value)} onKeyDown={event => event.key === 'Enter' && load()} /></div>
      <div className="support-filter" role="group" aria-label="Durum">{FILTERS.map(item => <button key={item.key} aria-pressed={filter === item.key} onClick={() => setFilter(item.key)}>{item.label}{item.key !== 'all' && <span>{counts[item.key]}</span>}</button>)}</div>
    </section>
    <div className="support-layout">
      <div className="support-card support-threads"><SupportThreadList threads={threads} loading={loading} selected={wide ? selected : null} open={open} /></div>
      {wide && <div className="support-card support-conversation">{selected ? <SupportConversation threadId={selected} onChanged={reloadQuietly} /> : <div className="support-empty h-full flex flex-col items-center justify-center"><MessageSquare size={28} className="text-gray-300" /><p className="mt-3">Soldan bir konuşma seçin</p></div>}</div>}
    </div>
    {!wide && selected && <PanelSheet title={selectedName || 'Konuşma'} close={() => setSelected(null)} wide><div className="support-sheet-body"><SupportConversation threadId={selected} onChanged={reloadQuietly} /></div></PanelSheet>}
  </div>;
}
